/**
 * Minimal layout for investor (Story 9-3).
 * Investor-only: app name, Dashboard, Settings, Logout. No admin nav or links to investments/ROI.
 */
import type { ReactNode } from "react";
import { Link, useNavigate, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../../store/authStore";
import { authService } from "../../services/authService";
import { ThemeToggle } from "../atoms/ThemeToggle";
import { Avatar } from "../atoms/Avatar";
import { Button } from "../atoms/Button";
import { LanguageSwitcher } from "../molecules/LanguageSwitcher";
import logoImage from "../../assets/global-clean-logo.png";

interface InvestorLayoutProps {
  children?: ReactNode;
}

export function InvestorLayout({ children }: InvestorLayoutProps) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { user, logout: logoutStore } = useAuthStore();

  const handleLogout = async () => {
    await authService.logout();
    logoutStore();
    navigate("/login");
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900">
        <div
          className="animate-spin rounded-full h-10 w-10 border-2 border-primary-600 border-t-transparent"
          role="status"
          aria-label={t("loading_aria")}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex flex-col">
      <header className="bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700 sticky top-0 z-40">
        <div className="px-4 sm:px-6">
          <div className="flex justify-between items-center h-16">
            <Link
              to="/investor"
              className="flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded"
              aria-label={t("investor_dashboard")}
            >
              <img src={logoImage} alt={t("logo_alt")} className="h-8 w-auto" />
            </Link>
            <div className="flex items-center gap-2 sm:gap-4">
              <ThemeToggle />
              <LanguageSwitcher />
              <Link
                to="/investor"
                className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
              >
                {t("dashboard")}
              </Link>
              <Link
                to="/investor/settings"
                className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
              >
                {t("nav_settings")}
              </Link>
              <Avatar name={user.fullName || user.email || user.pseudoLogin || "?"} size="sm" />
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                {t("logout")}
              </Button>
            </div>
          </div>
        </div>
      </header>
      <main className="flex-1">{children ?? <Outlet />}</main>
    </div>
  );
}
